import { tokenStore } from "./authTransport";
import { readSignIn } from "./twoFactorModel";
import { GOOGLE_CLIENT_ID, SIGN_IN_SCOPES } from "../../../shared/google-oauth-contract";
const CALLBACK = "https://app.itsbloxx.com/integrations/google/callback";
const KEY = "bloxx-google-sso";
type Attempt = { state: string; verifier: string; expiresAt: number };
async function request(path: string, body: unknown): Promise<Record<string, string>> {
  if(!import.meta.env.VITE_SCHEDULING_URL)throw new Error("Google sign-in is being configured. Use your password for now.");
  const base = new URL(import.meta.env.VITE_SCHEDULING_URL.replace(/\/?$/, "/")+"v1/");
  const response = await fetch(new URL(path, base), {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body), signal: AbortSignal.timeout(60000),
  });
  const value = await response.json().catch(() => null);
  if (!response.ok || !value || typeof value !== "object") throw new Error("Google sign-in could not finish. Try again, or sign in with your password.");
  return value;
}
function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
/**
 * The consent URL Xano built for sign-in, checked before the browser goes there: our client and
 * callback, this attempt's state and PKCE challenge, and EXACTLY the identity scopes. A URL asking for
 * anything more (Analytics, Calendar...) is refused, so sign-in can never again carry a scope that is
 * not part of the sign-in consent Google reviews.
 */
export function isSignInUrl(url: URL, state: string, challenge: string): boolean {
  const allowed=new Set(["client_id","redirect_uri","response_type","state","nonce","scope","access_type","include_granted_scopes","code_challenge","code_challenge_method"]);
  const scopes=(url.searchParams.get("scope")||"").split(" ").filter(Boolean).sort();
  return !(url.username || url.password || url.hash || [...url.searchParams.keys()].some(name=>!allowed.has(name)||url.searchParams.getAll(name).length!==1) ||
      scopes.join(" ") !== [...SIGN_IN_SCOPES].sort().join(" ") || url.searchParams.get("access_type")!=="online" ||
      url.origin !== "https://accounts.google.com" || url.pathname !== "/o/oauth2/v2/auth" ||
      url.searchParams.get("redirect_uri") !== CALLBACK || url.searchParams.get("client_id") !== GOOGLE_CLIENT_ID ||
      url.searchParams.get("state") !== state || url.searchParams.get("nonce") !== state ||
      url.searchParams.get("code_challenge") !== challenge || url.searchParams.get("code_challenge_method") !== "S256" ||
      url.searchParams.get("response_type") !== "code" || !state);
}

export async function startGoogleSignIn(): Promise<void> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
  const result = await request("auth/google/start", { challenge });
  const url = new URL(result.authorization_url);
  if (!isSignInUrl(url, result.state, challenge)) throw Error("Google sign-in configuration is unavailable.");
  const attempt: Attempt = {state: result.state, verifier, expiresAt: Date.now()+600000};
  sessionStorage.setItem(KEY, JSON.stringify(attempt));
  window.location.assign(url.toString());
}
// StrictMode can remount the callback; one browser attempt must make one exchange.
let completion: Promise<string | null> | undefined;
/** Resolves null once the session is stored, or with a two-step challenge to ask a code for. */
export function completeGoogleSignIn(params: URLSearchParams): Promise<string | null> {
  return completion ??= (async () => {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    history.replaceState(null, "", "/auth/google/callback");
    if (params.has("error")) throw Error("Google sign-in was cancelled. You can try again or use your password.");
    const attempt: Attempt | null = raw ? JSON.parse(raw) : null;
    if (!attempt || attempt.state !== params.get("state") || attempt.expiresAt <= Date.now() || !params.get("code"))
      throw Error("This sign-in attempt expired or belongs to another browser. Start again.");
    const result = await request("auth/google/callback", {state: attempt.state, verifier: attempt.verifier, code: params.get("code")});
    // Two-step sign-in: Google was the first factor; the code step makes the session.
    const answer = readSignIn(result);
    if (answer.kind === "code") return answer.challenge;
    tokenStore.set(answer.token);
    return null;
  })();
}
